import { addBreadcrumb, logger } from '@sentry/react';

import i18n from '@/config/i18n/i18n';

import { openOfficeDialog } from './officeDialogManager';
import { awaitDialogOutcome } from './officeDialogMessaging';

// Federated sign-in with MFA and a first-run consent screen has been observed
// well past 90s. Matches the connect dialog: the two ceremonies are the same
// shape and a user is no faster at one than the other.
const DIALOG_TIMEOUT_MS = 300_000;

/**
 * Office dialog event codes delivered on DialogEventReceived.
 *
 * 12006 means "closed as far as the host can tell" — a real user close, OR a
 * handle the host lost when an identity provider severed it mid-sign-in. It is
 * ambiguous and treated as such (LoginDialogLostError). Every other code is a
 * host or navigation failure and is definitively not a cancellation.
 */
const DIALOG_EVENT_CLOSED = 12_006;

/**
 * The host stopped tracking the login dialog. Deliberately NOT a cancellation:
 * the same 12006 arrives when a Cross-Origin-Opener-Policy header severs the
 * host's handle on a window the user is still signing into, and the login they
 * go on to complete has no way back through `messageParent`.
 *
 * The caller resolves the ambiguity by claiming the login handoff from the
 * backend — see `useAuthDialog`.
 */
export class LoginDialogLostError extends Error {
  constructor() {
    super('Lost track of the sign-in window.');
    this.name = 'LoginDialogLostError';
  }
}

/** The login dialog failed to run — a host or navigation failure, not a close. */
export class LoginDialogFailedError extends Error {
  /** Office DialogEventReceived code, when the host reported one. */
  readonly officeDialogErrorCode: number | undefined;

  constructor(message: string, officeDialogErrorCode?: number) {
    super(message);
    this.name = 'LoginDialogFailedError';
    this.officeDialogErrorCode = officeDialogErrorCode;
  }
}

/**
 * The dialog ran out of time without delivering a result. Distinct from a
 * plain failure because a completed login may still be sitting behind it: the
 * dialog stores the handoff before it attempts the relay, and a relay the
 * taskpane could not parse leaves the timeout as the only signal — so the
 * caller checks the handoff once before surfacing this.
 */
export class LoginDialogTimedOutError extends LoginDialogFailedError {
  constructor(message: string) {
    super(message);
    this.name = 'LoginDialogTimedOutError';
  }
}

/** What the login dialog produced, or how it failed to. */
export type LoginDialogResult = { token: string; type: 'bootstrap' } | { type: 'cancelled' };

/**
 * Logs in via an Office Dialog and returns a Clerk bootstrap session JWT.
 *
 * The dialog starts on the add-in's own origin (`taskpane.html?clerkAuth=1`),
 * redirects to the centralized auth app to perform a first-party Clerk login,
 * then returns to the add-in origin to `messageParent` the bootstrap token
 * back. This sidesteps the third-party-cookie blocking that prevents the
 * in-iframe taskpane on Outlook on the web from ever seeing a popup-based
 * login.
 *
 * `handoffId` is the insurance for when `messageParent` cannot land: the dialog
 * also posts its result to the backend under that id, so the caller can claim
 * the login even after the host loses the window. It rides the URL fragment,
 * which browsers never send to a server.
 *
 * Throws LoginDialogLostError when the host stopped tracking the window (ask
 * the backend before concluding anything), and LoginDialogFailedError for host
 * failures and timeouts.
 */
export async function loginViaOfficeDialog(handoffId: string): Promise<LoginDialogResult> {
  const url = new URL('/taskpane.html', window.location.origin);
  url.searchParams.set('clerkAuth', '1');
  url.hash = new URLSearchParams({ handoffId }).toString();

  addBreadcrumb({ message: '[ClerkAuthDialog] opening Office login dialog' });
  // Logged, not just breadcrumbed: a login that ends in cancellation captures
  // no error event, and breadcrumbs only ship attached to one — so without
  // this the whole attempt is invisible in Sentry.
  logger.info('Login: opening Office dialog');
  const dialog = await openOfficeDialog(url.toString(), { height: 60, width: 40 });

  const outcome = await awaitDialogOutcome(dialog, {
    label: 'ClerkAuthDialog',
    timeoutMs: DIALOG_TIMEOUT_MS,
  });

  if (outcome.type === 'timeout') {
    logger.warn('Login: dialog timed out without a result', {
      ignoredMessageCount: outcome.ignoredMessageCount,
    });
    throw new LoginDialogTimedOutError(i18n.t('login.error.timedOut'));
  }

  if (outcome.type === 'closed') {
    const { code } = outcome;

    if (code === DIALOG_EVENT_CLOSED) {
      // Ambiguous by design — see LoginDialogLostError. Either the user closed
      // the window, or an identity provider severed the host's handle on a
      // sign-in still in progress. The caller settles it against the backend.
      logger.warn('Login: host stopped tracking the sign-in window', {
        ignoredMessageCount: outcome.ignoredMessageCount,
        officeDialogErrorCode: code,
      });
      throw new LoginDialogLostError();
    }

    // 12002 (navigation failed), 12003 (non-HTTPS URL), 12004 (domain missing
    // from the manifest's AppDomains) and friends are host failures, not
    // cancellations — surface the code so a support screenshot is actionable.
    // oxlint-disable-next-line no-console
    console.error(
      `[ClerkAuthDialog] Office dialog failed with event code ${code ?? 'unknown'} (not a user close). 12002/12004 usually mean the sign-in URL is unreachable or missing from the manifest AppDomains.`,
    );
    throw new LoginDialogFailedError(
      i18n.t('login.error.dialogFailed', { code: code ?? 'unknown' }),
      code,
    );
  }

  const { message } = outcome;
  if (message.type === 'auth:bootstrap') {
    logger.info('Login: dialog relayed a bootstrap token');
    return { token: message.token, type: 'bootstrap' };
  }
  if (message.type === 'auth:error') {
    logger.warn('Login: dialog reported a sign-in failure');
    throw new LoginDialogFailedError(message.error);
  }
  // auth:cancel or auth:success without a token — user did not sign in.
  logger.warn('Login: dialog reported cancellation', { messageType: message.type });
  return { type: 'cancelled' };
}
